import React, {useEffect, useMemo, useState, useRef} from "react";
import {useExpanded, useFlexLayout, usePagination, useResizeColumns, useSortBy, useTable} from "react-table";
import {QueryClient, QueryClientProvider, useQuery} from 'react-query'
import {TABLE_COLUMNS} from "./columns";
import ReactTablePagination from '@/shared/components/table/components/ReactTablePagination';
import {Col, Row} from 'reactstrap';

import {withRouter} from "react-router-dom";
import {connect} from "react-redux";
import {
  reducer,
  useEffectToDispatch,
  fetchApiData,
  fetchApiDataExport,
  initialState,
  TableFilter,
  Sorting
} from "@/shared/apiTableHelper"
import {clone} from 'lodash';
import {getFirstDay, formatCurrency, getReportName} from '@/shared/helpers'
import {Sticky, StickyContainer} from 'react-sticky';

const formatDate = (date) => {
  if (!date) return null;
  if (date instanceof Date) {
    return date.toISOString().split('T')[0];  // valid Date
  }
  if (typeof date === 'string') {
    return date.split('T')[0];                // already string "YYYY-MM-DD..."
  }
  return null;
};
const queryClient = new QueryClient()

const DataTable = ({organization, programs}) => {
  const defaultFrom = getFirstDay();
  const [filter, setFilter] = useState({programs: programs, awardLevels: [], from: defaultFrom, to: new Date()});
  const [useFilter, setUseFilter] = useState(false);
  const [trigger, setTrigger] = useState(0);
  const [exportData, setExportData] = useState([]);
  const [exportHeaders, setExportHeaders] = useState([]);
  const [exportToCsv, setExportToCsv] = useState(false);
  const exportLink = React.createRef();
  const [isExporting, setIsExporting] = useState(false);
    const isFirstLoad = useRef(true);
  let columns = useMemo(() => TABLE_COLUMNS, [])

  const [{queryPageIndex, queryPageSize, totalCount, queryPageFilter, queryPageSortBy, queryTrigger}, dispatch] =
    React.useReducer(reducer, initialState);

  const apiUrl = `/organization/${organization.id}/report/cash-deposit`;
  const apiFilter = clone(filter);
  apiFilter.is_pay_in_advance = 1;
  if (
    isFirstLoad.current &&
    programs &&
    filter.programs.length === programs.length
  ) {
    apiFilter.programs = ["all"];
    isFirstLoad.current = false;
  }
apiFilter.from = formatDate(apiFilter.from);
apiFilter.to = formatDate(apiFilter.to);
  const {isLoading, error, data, isSuccess, isFetching} = useQuery(
  ['cash-deposit', apiUrl, queryPageIndex, queryPageSize, queryPageFilter, queryPageSortBy, queryTrigger],
  () => fetchApiData(
      {
        url: apiUrl,
        page: queryPageIndex,
        size: queryPageSize,
        filter: apiFilter,
        sortby: queryPageSortBy,
        trigger: queryTrigger
      }
    ),
    {
      keepPreviousData: true,
      staleTime: Infinity,
    }
  );

  useEffect(() => {
    if (exportToCsv) {
      if (exportLink.current) {
        setExportToCsv(false);
        exportLink.current.link.click();
      }
    }
  }, [exportLink])

  const download = async (filterValues) => {
    try {
      setIsExporting(true);
      let tmpFilter = clone(filterValues);
      tmpFilter.is_pay_in_advance = 1;
      if (tmpFilter.from) {
          tmpFilter.from = formatDate(tmpFilter.from);
      }
      if (tmpFilter.to) {
          tmpFilter.to = formatDate(tmpFilter.to);
      }
      tmpFilter.exportToCsv = 1;

      const response = await fetchApiDataExport(
        {
          url: apiUrl,
          filter: tmpFilter,
          sortby: queryPageSortBy,
          trigger: queryTrigger
        }
      );

      setExportData(response?.results || []);
      setExportHeaders(response?.headers || []);
      setExportToCsv(true);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setIsExporting(false);
    }
  }

  const totalPageCount = Math.ceil(totalCount / queryPageSize)

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    prepareRow,
    page,
    pageCount,
    pageOptions,
    gotoPage,
    previousPage,
    canPreviousPage,
    nextPage,
    canNextPage,
    setPageSize,
    state: {pageIndex, pageSize, sortBy}
  } = useTable({
      columns,
      data: data ? data.results : [],
      initialState: {
        pageIndex: queryPageIndex,
        pageSize: queryPageSize,
        sortBy: queryPageSortBy,
      },
      manualPagination: true, // Tell the usePagination
      manualSortBy: true, // server-side sorting: prevent react-table from sorting data client-side
      pageCount: data ? totalPageCount : null,
      autoResetSortBy: false,
      autoResetExpanded: false,
      autoResetPage: false,
      disableResizing: true
    },
    useSortBy,
    useExpanded,
    usePagination,
    useResizeColumns,
    useFlexLayout,
  );

  const manualPageSize = []
  useEffectToDispatch(dispatch, {pageIndex, pageSize, gotoPage, sortBy, filter, data, useFilter, trigger});

  if (error) {
    return <p>Error: {JSON.stringify(error)}</p>;
  }

  if (isLoading || !organization?.id) {
    return <p>Loading...</p>;
  }

  if (isSuccess)
    return (
      <StickyContainer>
        <div className='table react-table report-table'>
          <div className="action-panel">
            <Row className="form__form-group mx-0">
              <Col>
                <TableFilter filter={filter} setFilter={setFilter} setUseFilter={setUseFilter}
                             exportData={exportData} exportLink={exportLink} exportHeaders={exportHeaders}
                             download={download} isExporting={isExporting}
                             reportName={getReportName("CashDeposit")}
                             organization={organization}
                             config={{
                               keyword: false,
                               dateRange: true,
                               // awardLevels: availableAwardLevels,
                               programs: true,
                               programParams: 'is_pay_in_advance=1',
                               exportToCsv: true,
                               savedFilters: 'cash-deposit'
                             }}/>
              </Col>
            </Row>
            <div style={{clear: 'both'}}>&nbsp;</div>
          </div>
          {
            (isLoading || isFetching) && <p className="text-center">Loading...</p>
          }
          {
            // ref={r => { csvLinkTable = r; }}
            isSuccess &&
            <table {...getTableProps()} className="table">
                <Sticky  topOffset={80}>
                  {({ style }) => (
                    <thead style={{...style, top: '60px'}}> 
                      {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                          {headerGroup.headers.map(column => (
                            (() => {
                              const sortToggleProps = column.getSortByToggleProps ? column.getSortByToggleProps() : {};
                              const mergedProps = {
                                ...sortToggleProps,
                                onClick: (e) => {
                                  if (sortToggleProps.onClick) sortToggleProps.onClick(e);
                                  if (gotoPage) gotoPage(0);
                                  setTrigger(t => t + 1);
                                }
                              };
                              return (
                                <th {...column.getHeaderProps(mergedProps)}>
                                  {column.render('Header')}
                                  {column.isSorted ? <Sorting column={column}/> : ''}
                                </th>
                              )
                            })()
                          ))}
                        </tr>
                      ))}
                    </thead>
                  )}
                </Sticky>
              <tbody className="table table--bordered" {...getTableBodyProps()}>
              {page.map(row => {
                prepareRow(row);
                const subCount = (row.id.match(/\./g) || []).length
                return (
                  <tr {...row.getRowProps()}>
                    {
                      row.cells.map(cell => {
                        // console.log(cell)
                        const paddingLeft = subCount * 20
                        return <td {...cell.getCellProps()}><span
                          style={cell.column.Header === '#' ? {paddingLeft: `${paddingLeft}px`} : null}>{cell.render('Cell')}</span>
                        </td>
                      })
                    }
                  </tr>
                )
              })}
              </tbody>
              <tfoot>
              {footerGroups.map((footerGroup) => (
                <tr {...footerGroup.getFooterGroupProps()}>
                  {footerGroup.headers.map(column => (
                    <th {...column.getFooterProps()}>{column.render('Footer')}</th>
                  ))}
                </tr>
              ))}
              </tfoot>
            </table>
          }

          {(rows.length > 0) && (
            <>
              <ReactTablePagination
                page={page}
                gotoPage={gotoPage}
                previousPage={previousPage}
                nextPage={nextPage}
                canPreviousPage={canPreviousPage}
                canNextPage={canNextPage}
                pageOptions={pageOptions}
                pageSize={pageSize}
                pageIndex={pageIndex}
                pageCount={pageCount}
                setPageSize={setPageSize}
                manualPageSize={manualPageSize}
                dataLength={totalCount}
              />
              <div className="pagination justify-content-end mt-2">
                            <span>
                            Go to page:{' '}
                              <input
                                type="number"
                                value={pageIndex + 1}
                                onChange={(e) => {
                                  const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                  gotoPage(page);
                                }}
                                style={{width: '100px'}}
                              />
                            </span>{' '}
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                  }}
                >
                  {[10, 20, 30, 40, 50].map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      Show {pageSize}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>
      </StickyContainer>
    )
}

const TableWrapper = ({organization, programs}) => {
  if (!organization || !programs) return 'Loading...'
  return (
    <QueryClientProvider client={queryClient}>
        <DataTable organization={organization} programs={programs}/>
    </QueryClientProvider>
  )
}

export default withRouter(connect((state) => ({
  organization: state.organization
}))(TableWrapper));